import React, { useState, useEffect } from 'react';
import { X, Maximize, Minimize, Clock } from 'lucide-react';
import Dropdown from '../../../components/Dropdown';
import PermissionDeniedModal from '../../../components/Modals/PermissionDeniedModal';

const InitializeJobModal = ({ isOpen, onClose, onSubmit, prefillData, canTriggerJob, canScheduleJob }) => {
  const [jobData, setJobData] = useState({
    gitHubRepo: '',
    branchToUse: '',
    jobName: '',
    noOfTasks: '',
    containerParams: '',
    jobType: 'ondemand', // Default to on-demand jobs
    cronExpression: ''
  });
  
  const [repositories, setRepositories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [fullscreenParams, setFullscreenParams] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [deniedAction, setDeniedAction] = useState('');

  useEffect(() => {
    const fetchGithubRepos = async () => {
      try {
        const response = await fetch(`https://cloudify-prod.wizr.in/get_org_repos`);
        const data = await response.json();
        setRepositories(data);
      } catch (error) {
        console.error('Error fetching repositories:', error);
      }
    };

    if (isOpen) {
      fetchGithubRepos();
    }
  }, [isOpen]);

  // Handle prefilled data if provided
  useEffect(() => {
    if (prefillData) {
      setJobData({
        gitHubRepo: prefillData.GITHUB_REPO || '',
        branchToUse: prefillData.BRANCH_TO_USE || '',
        jobName: prefillData.JOB_NAME || '',
        noOfTasks: prefillData.NO_OF_TASKS || '',
        containerParams: prefillData.CONTAINER_PARAMS || '',
        jobType: prefillData.JOB_TYPE || 'ondemand',
        cronExpression: prefillData.CRON_EXPRESSION || ''
      });
      
      // If there's a repository selected, fetch its branches
      if (prefillData.GITHUB_REPO) {
        fetchBranches(prefillData.GITHUB_REPO);
      }
    }
  }, [prefillData]);

  useEffect(() => {
    const fetchBranchesForRepo = async () => {
      if (jobData.gitHubRepo) {
        fetchBranches(jobData.gitHubRepo);
      }
    };

    fetchBranchesForRepo();
  }, [jobData.gitHubRepo]);

  const fetchBranches = async (repo) => {
    try {
      const response = await fetch(`https://cloudify-prod.wizr.in/${repo}/get_branches`);
      const data = await response.json();
      setBranches(data);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if( jobData.jobType === 'scheduled' && !canScheduleJob) {
      setPermissionDenied(true);
      setDeniedAction('scheduled jobs');
      return;
    }
    if ( jobData.jobType === 'ondemand' && !canTriggerJob) {
      setPermissionDenied(true);
      setDeniedAction('trigger jobs');
      return;
    } 
    onSubmit(jobData);
    setJobData({
      gitHubRepo: '',
      branchToUse: '',
      jobName: '',
      noOfTasks: '',
      containerParams: '',
      jobType: 'ondemand',
      cronExpression: ''
    });
    setFullscreenParams(false);
    onClose();
  };

  const handleCancel = () => {
    setJobData({
      gitHubRepo: '',
      branchToUse: '',
      jobName: '',
      noOfTasks: '',
      containerParams: '',
      jobType: 'ondemand',
      cronExpression: ''
    });
    setFullscreenParams(false);
    onClose();
  };

  const toggleFullscreenParams = () => {
    setFullscreenParams(!fullscreenParams);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm transition-opacity"></div>

      {/* Modal */}
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className={`relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all ${fullscreenParams ? 'w-11/12 h-5/6' : 'w-full max-w-2xl'}`}>
          {/* Header */}
          <div className="bg-gray-900 px-4 py-3 flex justify-between items-center sticky top-0 z-10">
            <h3 className="text-lg font-medium text-white">
              {prefillData ? 'Reuse Job Configuration' : 'Initialize New Job'}
            </h3>
            <button
              onClick={handleCancel}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-700 hover:text-white focus:outline-none"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className={`p-6 overflow-y-auto ${fullscreenParams ? 'h-[calc(100%-120px)]' : 'max-h-[70vh]'}`}>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Job Type Selection */}
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Job Type
                </label>
                <div className="flex items-center space-x-4">
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-indigo-600"
                      name="jobType"
                      value="ondemand"
                      checked={jobData.jobType === 'ondemand'}
                      onChange={() => setJobData({ ...jobData, jobType: 'ondemand' })}
                    />
                    <span className="ml-2">On-Demand</span>
                  </label>
                  <label className="inline-flex items-center">
                    <input
                      type="radio"
                      className="form-radio text-indigo-600"
                      name="jobType"
                      value="scheduled"
                      checked={jobData.jobType === 'scheduled'}
                      onChange={() => setJobData({ ...jobData, jobType: 'scheduled' })}
                    />
                    <span className="ml-2">Scheduled</span>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  GitHub Repository
                </label>
                <Dropdown
                  items={repositories}
                  value={jobData.gitHubRepo}
                  onSelect={(repo) => setJobData({ ...jobData, gitHubRepo: repo })}
                  placeholder="Search GitHub repositories..."
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Branch To Use
                </label>
                <Dropdown
                  items={branches}
                  value={jobData.branchToUse}
                  onSelect={(branch) => setJobData({ ...jobData, branchToUse: branch })}
                  placeholder="Search branches..."
                  disabled={!jobData.gitHubRepo}
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Job Name
                </label>
                <input
                  type="text"
                  value={jobData.jobName}
                  onChange={(e) => setJobData({ ...jobData, jobName: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter job name"
                />
              </div>

              <div>
                <label className="text-gray-700 text-sm font-medium block mb-1">
                  Number of Tasks
                </label>
                <input
                  type="number"
                  min="1"
                  value={jobData.noOfTasks}
                  onChange={(e) => setJobData({ ...jobData, noOfTasks: e.target.value })}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  placeholder="Enter number of tasks"
                />
              </div>

              {/* Conditional cron expression field */}
              {jobData.jobType === 'scheduled' && (
                <div>
                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-indigo-500 mr-2" />
                    <label className="text-gray-700 text-sm font-medium block">
                      Cron Expression
                    </label>
                  </div>
                  <input
                    type="text"
                    value={jobData.cronExpression}
                    onChange={(e) => setJobData({ ...jobData, cronExpression: e.target.value })}
                    className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 mt-1"
                    placeholder="e.g., 0 0 * * * (midnight every day)"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Format: cron(Minutes Hours Day-of-month Month Day-of-week Year)

                  </p>
                </div>
              )}

              <div>
                <div className="flex justify-between items-center mb-1">
                  <label className="text-gray-700 text-sm font-medium">
                    Container Parameters
                  </label>
                  <button
                    type="button"
                    onClick={toggleFullscreenParams}
                    className="text-gray-500 hover:text-gray-700 p-1 rounded focus:outline-none"
                  >
                    {fullscreenParams ? <Minimize size={18} /> : <Maximize size={18} />}
                  </button>
                </div>
                <textarea
                  value={jobData.containerParams}
                  onChange={(e) => setJobData({ ...jobData, containerParams: e.target.value })}
                  rows={fullscreenParams ? 20 : 6}
                  className="w-full px-3 py-2 border rounded-md focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 font-mono text-sm"
                  placeholder='[{"name": "SUBJECT", "value": "60,51,7"}, {"name": "SUBJECT", "value": "10,4,13"}]'
                />
                <p className="text-xs text-gray-500 mt-1">
                  JSON format for container parameters. Enter key-value pairs in proper JSON format.
                </p>
              </div>
            </form>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 sticky bottom-0 z-10">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={handleSubmit}
                className="inline-flex justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                {prefillData ? 'Reuse & Initialize Job' : jobData.jobType === 'scheduled' ? 'Schedule Job' : 'Initialize Job'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <PermissionDeniedModal
        visible={permissionDenied}
        onClose={() => setPermissionDenied(false)}
        message={`You don't have permission to ${deniedAction}. Please contact your administrator.`}
      />
    </div>
  );
};

export default InitializeJobModal;